import React, { useCallback, useEffect, useState } from "react";
import PolarisAdminSelect from "./PolarisAdminSelect";
import PolarisConfirmModal from "./PolarisConfirmModal";
import {
    FeatureIdeaStatus,
    FeatureIdeaSummary,
    fetchPolarisFeatureIdeas,
    formatRelativeTime,
    markPolarisFeatureIdeaComplete,
    reopenPolarisFeatureIdea,
} from "../services/featureRequestsService";
import "../styles/polaris-admin.css";

const PAGE_SIZE = 20;

export default function PolarisFeatureIdeasPage() {
    const [statusFilter, setStatusFilter] = useState<FeatureIdeaStatus | "">("ACTIVE");
    const [ideas, setIdeas] = useState<FeatureIdeaSummary[]>([]);
    const [page, setPage] = useState(0);
    const [totalPages, setTotalPages] = useState(0);
    const [loading, setLoading] = useState(true);
    const [paging, setPaging] = useState(false);
    const [error, setError] = useState<string | null>(null);
    const [actionId, setActionId] = useState<number | null>(null);
    const [confirmIdea, setConfirmIdea] = useState<FeatureIdeaSummary | null>(null);
    const [confirmAction, setConfirmAction] = useState<"complete" | "reopen" | null>(null);

    const load = useCallback(async (pageNum: number, status: FeatureIdeaStatus | "", isPaging = false) => {
        if (isPaging) setPaging(true);
        else setLoading(true);
        setError(null);
        try {
            const data = await fetchPolarisFeatureIdeas(status || undefined, pageNum, PAGE_SIZE);
            setIdeas(data.content);
            setTotalPages(data.totalPages);
            setPage(data.number);
        } catch (e: unknown) {
            setError(e instanceof Error ? e.message : "Failed to load feature ideas.");
        } finally {
            setLoading(false);
            setPaging(false);
        }
    }, []);

    useEffect(() => {
        load(0, statusFilter);
    }, [load, statusFilter]);

    const runAction = async () => {
        if (!confirmIdea || !confirmAction) return;
        setActionId(confirmIdea.id);
        setError(null);
        try {
            if (confirmAction === "complete") {
                await markPolarisFeatureIdeaComplete(confirmIdea.id);
            } else {
                await reopenPolarisFeatureIdea(confirmIdea.id);
            }
            setConfirmIdea(null);
            setConfirmAction(null);
            await load(page, statusFilter, true);
        } catch (e: unknown) {
            setError(e instanceof Error ? e.message : "Failed to update idea.");
        } finally {
            setActionId(null);
        }
    };

    return (
        <section className="pa-section" data-testid="polaris-feature-ideas-page">
            <h1 className="pa-page-title">Feature Ideas</h1>
            <p style={{ color: "#6b7280", margin: "0 0 1.5rem" }}>
                Review community posts from administrators, staff, and coaches. Mark posts complete to
                move them to the Completed tab on the Community Forum.
            </p>

            <div style={{ display: "flex", gap: "0.75rem", marginBottom: "1rem", flexWrap: "wrap" }}>
                <label style={{ display: "flex", flexDirection: "column", gap: "0.35rem" }}>
                    <span>Status</span>
                    <PolarisAdminSelect
                        size="compact"
                        value={statusFilter}
                        options={[
                            { value: "", label: "All" },
                            { value: "ACTIVE", label: "Active" },
                            { value: "COMPLETED", label: "Completed" },
                        ]}
                        onChange={(next) => {
                            setStatusFilter(next as FeatureIdeaStatus | "");
                            setPage(0);
                        }}
                        data-testid="polaris-feature-ideas-status-filter"
                    />
                </label>
            </div>

            {error && <div className="pa-error">{error}</div>}
            {loading && <div className="pa-loading">Loading feature ideas…</div>}

            {!loading && (
                <div className="pa-table-wrapper" style={{ opacity: paging ? 0.6 : 1 }}>
                    <table className="pa-table">
                        <thead>
                            <tr>
                                <th>Title</th>
                                <th>Author</th>
                                <th>Company</th>
                                <th>Votes</th>
                                <th>Status</th>
                                <th>Created</th>
                                <th>Actions</th>
                            </tr>
                        </thead>
                        <tbody>
                            {ideas.length === 0 && (
                                <tr>
                                    <td colSpan={7}>No feature ideas found.</td>
                                </tr>
                            )}
                            {ideas.map((idea) => (
                                <tr key={idea.id} data-testid={`polaris-feature-idea-row-${idea.id}`}>
                                    <td>
                                        <strong>{idea.title}</strong>
                                        <div style={{ color: "#6b7280", fontSize: "0.85rem", marginTop: 4 }}>
                                            {idea.description.length > 120
                                                ? `${idea.description.slice(0, 120)}…`
                                                : idea.description}
                                        </div>
                                    </td>
                                    <td>{idea.authorDisplayName}</td>
                                    <td>{idea.authorCompanyName || "—"}</td>
                                    <td>{idea.voteScore}</td>
                                    <td>
                                        <span
                                            className={`pa-badge ${
                                                idea.status === "ACTIVE" ? "pa-badge-active" : "pa-badge-inactive"
                                            }`}
                                        >
                                            {idea.status === "ACTIVE" ? "Active" : "Completed"}
                                        </span>
                                    </td>
                                    <td>{formatRelativeTime(idea.createdAt)}</td>
                                    <td>
                                        <div className="pa-actions">
                                            {idea.status === "ACTIVE" ? (
                                                <button
                                                    type="button"
                                                    className="pa-page-btn"
                                                    disabled={actionId === idea.id}
                                                    data-testid={`polaris-feature-idea-complete-${idea.id}`}
                                                    onClick={() => {
                                                        setConfirmIdea(idea);
                                                        setConfirmAction("complete");
                                                    }}
                                                >
                                                    Mark complete
                                                </button>
                                            ) : (
                                                <button
                                                    type="button"
                                                    className="pa-page-btn"
                                                    disabled={actionId === idea.id}
                                                    data-testid={`polaris-feature-idea-reopen-${idea.id}`}
                                                    onClick={() => {
                                                        setConfirmIdea(idea);
                                                        setConfirmAction("reopen");
                                                    }}
                                                >
                                                    Reopen
                                                </button>
                                            )}
                                        </div>
                                    </td>
                                </tr>
                            ))}
                        </tbody>
                    </table>

                    {totalPages > 1 && (
                        <div className="pa-pagination" style={{ marginTop: "1rem", display: "flex", gap: 8 }}>
                            <button
                                type="button"
                                className="pa-page-btn"
                                disabled={page <= 0 || paging}
                                onClick={() => load(page - 1, statusFilter, true)}
                            >
                                Previous
                            </button>
                            <span style={{ alignSelf: "center", color: "#6b7280" }}>
                                Page {page + 1} of {totalPages}
                            </span>
                            <button
                                type="button"
                                className="pa-page-btn"
                                disabled={page >= totalPages - 1 || paging}
                                onClick={() => load(page + 1, statusFilter, true)}
                            >
                                Next
                            </button>
                        </div>
                    )}
                </div>
            )}

            <PolarisConfirmModal
                open={Boolean(confirmIdea && confirmAction)}
                title={confirmAction === "complete" ? "Mark post complete?" : "Reopen post?"}
                message={
                    confirmAction === "complete"
                        ? `Mark "${confirmIdea?.title}" as complete? It will move to the Completed tab on the Community Forum.`
                        : `Reopen "${confirmIdea?.title}"? It will return to Active posts.`
                }
                confirmLabel={confirmAction === "complete" ? "Mark complete" : "Reopen"}
                onConfirm={runAction}
                onClose={() => {
                    setConfirmIdea(null);
                    setConfirmAction(null);
                }}
            />
        </section>
    );
}
